/*
 * Fancybox (about 42 KB transfer, 149 KB parsed) only powers the PDP lightbox, so it is no longer a
 * parse-time script. sections/main-product.liquid exposes the asset URLs on product-info and keeps
 * fancybox.css as a non-blocking sheet at its cascade position. The script is warmed when the browser
 * is idle after load and on the first touch of the gallery, and the lightbox waits for both the script
 * and the sheet. This file can run more than once (quick view, product swaps), so state lives on window
 * and there are no top-level let/const declarations.
 */
function hbFancyboxCssReady(host) {
  return new Promise((resolve) => {
    let link = document.querySelector("link[data-hb-fancybox-css]");
    if (!link) {
      if (!host?.dataset.fancyboxCss) return resolve();
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = host.dataset.fancyboxCss;
      link.setAttribute("data-hb-fancybox-css", "");
      // Fancybox rules must precede the section-main-product.css overrides.
      const anchor = document.querySelector('link[href*="section-main-product.css"]');
      if (anchor) anchor.parentNode.insertBefore(link, anchor);
      else document.head.appendChild(link);
    }
    if (link.media !== "all") link.media = "all";
    // Readable rules, not link.sheet alone: Firefox sets link.sheet while the file is still loading
    // (the asset is same-origin, so the rules can be read once it has).
    try {
      if (link.sheet && link.sheet.cssRules) return resolve();
    } catch (error) {
      // Firefox throws InvalidAccessError while the file loads. SecurityError is a cross-origin copy
      // (a CDN host, say) that has loaded but whose rules can never be read; waiting for a load event
      // that has passed would only cost the 3s timeout.
      if (error?.name === "SecurityError") return resolve();
    }
    link.addEventListener("load", () => resolve(), { once: true });
    link.addEventListener("error", () => resolve(), { once: true });
    // A sheet that already failed never fires again; do not hold the lightbox forever.
    setTimeout(resolve, 3000);
  });
}

function hbLoadFancybox() {
  // The shared promise comes first: the script can define window.Fancybox before the sheet has
  // loaded, and a lightbox opened then would be unstyled.
  if (window.__hbFancyboxPromise) return window.__hbFancyboxPromise;

  const host = document.querySelector("[data-fancybox-js]");
  // A Fancybox that something else defined (an app, say) waits for the same sheet.
  if (window.Fancybox) {
    window.__hbFancyboxPromise = hbFancyboxCssReady(host).then(() => window.Fancybox);
    return window.__hbFancyboxPromise;
  }
  if (!host || !host.dataset.fancyboxJs) return Promise.reject(new Error("fancybox assets missing"));

  const cssReady = hbFancyboxCssReady(host);

  const jsReady = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = host.dataset.fancyboxJs;
    script.async = true;
    script.onload = () => (window.Fancybox ? resolve() : reject(new Error("Fancybox global missing")));
    script.onerror = () => reject(new Error("fancybox load failed"));
    document.head.appendChild(script);
  });

  window.__hbFancyboxPromise = Promise.all([cssReady, jsReady])
    .then(() => window.Fancybox)
    .catch((error) => {
      window.__hbFancyboxPromise = null;
      throw error;
    });
  return window.__hbFancyboxPromise;
}

if (!window.__hbFancyboxWarmupBound) {
  window.__hbFancyboxWarmupBound = true;
  const hbWarmFancybox = () => {
    if (!document.querySelector("[data-fancybox-js]")) return;
    const warm = () => hbLoadFancybox().catch(() => {});
    if ("requestIdleCallback" in window) window.requestIdleCallback(warm, { timeout: 4000 });
    else window.setTimeout(warm, 2500);
  };
  if (document.readyState === "complete") hbWarmFancybox();
  else window.addEventListener("load", hbWarmFancybox, { once: true });
}

function clearThumbnailInlineSizing(root = document) {
  root
    .querySelectorAll(".product--thumbnail_slider .product-media-container .product__media.media.loaded")
    .forEach((media) => media.style.removeProperty("padding-top"));
  // A media query, not window.innerWidth: reading innerWidth right after the classes above change forces a
  // style and layout pass, and this runs inside the shade tap that swaps the gallery (INP).
  if (window.matchMedia("(min-width: 750px)").matches) {
    root
      .querySelectorAll(".grid__item.product__media-wrapper slider-component.thumbnail-slider")
      .forEach((slider) => slider.style.removeProperty("width"));
  }
}

function applyThumbnailSliderEnhancements(root = document) {
  const matchWidths = () => clearThumbnailInlineSizing(root);
  if (!window.__hbThumbnailSizingBound) {
    window.__hbThumbnailSizingBound = true;
    let resizeTimeout;
    window.addEventListener("load", () => clearThumbnailInlineSizing(), { once: true });
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => clearThumbnailInlineSizing(), 250);
    });
  }

  root.querySelectorAll(".grid__item.product__media-wrapper slider-component.thumbnail-slider").forEach((slider) => {
    if (slider.__hbThumbnailHoverBound) return;
    slider.__hbThumbnailHoverBound = true;
    slider.addEventListener("mouseenter", (event) => {
      // Arrows only when the strip actually scrolls: with a few
      // thumbnails they showed on hover with nothing to scroll to.
      const list = event.currentTarget.querySelector(".thumbnail-list");
      if (!list || list.scrollWidth <= list.clientWidth + 1) return;
      event.currentTarget.querySelector(".slider-button.slider-button--prev")?.classList.add("show");
      event.currentTarget.querySelector(".slider-button.slider-button--next")?.classList.add("show");
    });
    slider.addEventListener("mouseleave", (event) => {
      event.currentTarget.querySelector(".slider-button.slider-button--prev")?.classList.remove("show");
      event.currentTarget.querySelector(".slider-button.slider-button--next")?.classList.remove("show");
    });
  });
  root.querySelectorAll(".thumbnail-slider .thumbnail").forEach((thumbnail) => {
    if (thumbnail.__hbThumbnailClickBound) return;
    thumbnail.__hbThumbnailClickBound = true;
    let selectTimeOut;
    thumbnail.addEventListener("click", function () {
      clearTimeout(selectTimeOut);
      selectTimeOut = setTimeout(matchWidths, 250);
    });
  });

  const revealImage = (image) => {
    image?.closest(".lazy-image")?.classList.add("loaded");
    const skeleton = image?.closest(".lazy-image")?.previousElementSibling;
    if (skeleton && skeleton.classList.contains("image-skeleton")) {
      skeleton.classList.add("hidden");
    }
  };
  const processSkeleton = (image) => {
    revealImage(image);
    matchWidths();
  };

  // Images that have already loaded (a cached gallery swapped in on a shade tap) are revealed together and
  // the inline sizing is cleared once for all of them, not once per image.
  let revealedLoaded = false;
  const lazyImages = root.querySelectorAll(".lazy-image img");
  lazyImages.forEach((image) => {
    if (image.naturalWidth > 0) {
      revealImage(image);
      revealedLoaded = true;
    } else {
      if (image.__hbSkeletonLoadBound) return;
      image.__hbSkeletonLoadBound = true;
      image.addEventListener(
        "load",
        function () {
          processSkeleton(image);
        },
        { once: true },
      );
    }
  });
  if (revealedLoaded) matchWidths();
}

function applyProductMediaEnhancements(root = document) {
  const productMediaLists = root.querySelectorAll(".product__media-list");
  productMediaLists.forEach((productMediaList) => {
    const nearestQuickAddModal = productMediaList?.closest(".quick-add-modal");

    if (nearestQuickAddModal) {
      const slider = nearestQuickAddModal.querySelector(".slider");
      if (slider && !slider.classList.contains("drag-initialized")) {
        slider.classList.add("drag-initialized");

        const sliderInstance = slider.closest("slider-component");
        if (sliderInstance) {
          sliderInstance.addDragToScroll();
        }
      }
      return;
    }

    if (productMediaList.__hbMediaEnhancementsBound) return;
    productMediaList.__hbMediaEnhancementsBound = true;

    // Pointerdown precedes the mouseup that opens the lightbox, so this usually hides the load.
    productMediaList.addEventListener("pointerdown", () => hbLoadFancybox().catch(() => {}), {
      once: true,
      passive: true,
    });

    let isDragging = false;

    productMediaList?.addEventListener("mousedown", () => {
      isDragging = false;
    });

    productMediaList?.addEventListener("mousemove", () => {
      isDragging = true;
    });

    const openLightbox = (event, triggerEl = null) => {
      if ((!isDragging || triggerEl) && event.button !== 2) {
        const thumbnailListItems = productMediaList
          .closest(".product__media-wrapper")
          .querySelectorAll(".thumbnail-list__item:not(.thumbnail-list_item--variant:not(:first-child)) img");
        const listItems = productMediaList
          .closest(".product__media-wrapper")
          .querySelectorAll(".thumbnail-list__item:not(.thumbnail-list_item--variant:not(:first-child))");

        let index = Array.from(listItems).findIndex(
          (item) => item.querySelector('button[aria-current="true"]') !== null,
        );

        let images;
        // Alt text per slide, so the zoomed image keeps the product image alt (Fancybox would name it "Image N of M")
        // and a video player is named after its video (Fancybox's own <video> has no name).
        const alts = [];

        if (thumbnailListItems.length > 0) {
          if (triggerEl) {
            const mediaId = triggerEl.closest(".product__media-item")?.dataset.mediaId;
            const triggerIndex = Array.from(listItems).findIndex((item) => item.dataset.target === mediaId);
            if (triggerIndex >= 0) index = triggerIndex;
          }
          images = Array.from(thumbnailListItems).map((img) => {
            const mediaContainer = img.closest(".thumbnail-list__item");
            const mediaType = mediaContainer?.dataset.mediaType;
            alts.push(img.alt || "");

            if (mediaType === "video") {
              const videoUrl = mediaContainer.dataset.videoUrl;
              return {
                src: `${videoUrl}`,
                thumb: img.src,
              };
            } else {
              return {
                src: img.src.replace(/(width=)\d+/i, "$11024"),
                thumb: img.src,
              };
            }
          });
        } else {
          index = 0;
          const mediaItems = productMediaList.querySelectorAll(".product__media-item");
          const lightboxMediaItems = [];
          images = Array.from(mediaItems)
            .map((item) => {
              const mediaType = item.dataset.mediaType;

              if (mediaType === "video" || mediaType === "external_video") {
                const videoUrl = item.dataset.videoUrl;
                const thumb = item.querySelector(".deferred-media__poster img");
                if (!videoUrl) return null;
                lightboxMediaItems.push(item);
                // The slide's own player (snippets/product-thumbnail.liquid) is already named.
                const player = item.querySelector("deferred-media template")?.content?.querySelector("video, iframe");
                alts.push(player?.getAttribute("aria-label") || player?.getAttribute("title") || "");
                return { src: videoUrl, thumb: thumb ? thumb.src : "" };
              } else {
                const img = item.querySelector(".lazy-image img");
                if (!img) return null;
                lightboxMediaItems.push(item);
                alts.push(img.alt || "");
                return { src: img.src.replace(/(width=)\d+/i, "$11024"), thumb: img.src };
              }
            })
            .filter(Boolean);
          if (triggerEl) {
            const triggerIndex = lightboxMediaItems.indexOf(triggerEl.closest(".product__media-item"));
            if (triggerIndex >= 0) index = triggerIndex;
          }
        }

        window.pauseAllMedia();
        if (!triggerEl) {
          const captureClick = (e) => {
            e.stopPropagation();
            e.preventDefault();
          };
          window.addEventListener("click", captureClick, { capture: true, once: true });
          setTimeout(() => window.removeEventListener("click", captureClick, true), 100);
        }

        // One lightbox per tap even if the shopper taps again while Fancybox is still loading.
        if (productMediaList.__hbLightboxOpening) return;
        productMediaList.__hbLightboxOpening = true;
        hbLoadFancybox()
          .then((Fancybox) => {
            // Translated close button and dialog name from the gallery (Fancybox defaults to English).
            const gallery = productMediaList.closest("media-gallery");
            const galleryL10n =
              gallery?.dataset.lightboxClose && gallery?.dataset.lightboxLabel
                ? { CLOSE: gallery.dataset.lightboxClose, MODAL: gallery.dataset.lightboxLabel }
                : null;
            // Every language but English also gets the locale's next, previous and go-to-slide labels.
            const localeL10n = window.accessibilityStrings?.lightbox;
            const lightboxL10n = galleryL10n || localeL10n ? { ...localeL10n, ...galleryL10n } : null;
            // RTL (Arabic): slides and thumbnails start on the right.
            const rtl = document.documentElement.dir === "rtl" ? { direction: "rtl" } : {};
            const box = new Fancybox(images, {
              ...(lightboxL10n ? { l10n: lightboxL10n } : {}),
              triggerEl,
              keyboard: {
                ArrowUp: false,
                ArrowDown: false,
                ArrowRight: false,
                ArrowLeft: false,
              },
              Thumbs: {
                type: "classic",
                autoStart: window.innerWidth <= 749,
                startIndex: index,
                Carousel: {
                  ...rtl,
                  // RTL: all thumbnails are rendered up front. Opened on a later image, Fancybox rendered the
                  // wrong ones in RTL and the strip stayed blank until the next swipe.
                  ...(rtl.direction ? { preload: 100 } : {}),
                  breakpoints: {
                    "(min-width: 750px)": {
                      axis: "y",
                    },
                  },
                },
              },
              startIndex: index,
              Carousel: {
                ...rtl,
                friction: 0,
                Panzoom: {
                  touch: false,
                  drag: false,
                },
              },
              Panzoom: {
                touch: false,
                drag: false,
              },

              dragToClose: false,
            });

            // Opened from the keyboard (Enter or Space on the image's button): closing returns focus there.
            if (triggerEl) {
              box.on("destroy", () => {
                if (triggerEl.isConnected) triggerEl.focus({ preventScroll: true });
              });
            }

            // RTL: Fancybox picks the slides to render before its pan position is ready and mirrors the page
            // in RTL, so a lightbox opened on a later image stayed blank. Pick them again once it is ready.
            if (rtl.direction) box.on("Carousel.Panzoom.ready", (fb) => fb.carousel?.manageSlideVisiblity?.());

            box.on("reveal", (_fb, slide) => {
              if (slide?.imageEl && alts[slide.index]) slide.imageEl.alt = alts[slide.index];
              const lightboxVideo = slide?.el?.querySelector("video");
              if (lightboxVideo && alts[slide.index]) lightboxVideo.setAttribute("aria-label", alts[slide.index]);
              setTimeout(() => {
                const videoThumbs = document.querySelectorAll(".f-thumbs__slide.for-video:not(.has-badge)");
                videoThumbs.forEach((thumb) => {
                  const badge = document.createElement("span");
                  badge.className = "thumbnail__badge";
                  badge.setAttribute("aria-hidden", "true");
                  badge.innerHTML = `
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-play" viewBox="0 0 10 14">
                  <path fill="currentColor" fill-rule="evenodd" d="M1.482.815A1 1 0 0 0 0 1.69v10.517a1 1 0 0 0 1.525.851L10.54 7.5a1 1 0 0 0-.043-1.728z" clip-rule="evenodd"></path>
                </svg>
              </span>
            `;

                  thumb.appendChild(badge);
                  thumb.classList.add("has-badge");
                });
              }, 100);
            });

            const onLightboxKeydown = (e) => {
              const key = rtl.direction ? { ArrowLeft: "ArrowRight", ArrowRight: "ArrowLeft" }[e.key] || e.key : e.key;
              if (Fancybox.getInstance()) {
                const box = Fancybox.getInstance();
                if (
                  ((key === "ArrowUp" || key === "ArrowLeft") && box?.carousel?.page === 0) ||
                  ((key === "ArrowDown" || key === "ArrowRight") &&
                    box?.carousel?.page === box?.carousel?.slides?.length - 1)
                ) {
                  box?.carousel?.slideTo(box?.carousel?.page);
                } else if (key === "ArrowUp" || key === "ArrowLeft") {
                  box?.carousel?.slidePrev();
                } else if (key === "ArrowDown" || key === "ArrowRight") {
                  box?.carousel?.slideNext();
                }
              }
            };

            const onLightboxWheel = (event) => {
              const deltaY = event.deltaY;
              if (Fancybox.getInstance()) {
                const box = Fancybox.getInstance();
                if (deltaY > 0 && box?.carousel?.page === 0) {
                  box?.carousel?.slidePrev();
                } else if (deltaY < 0 && box?.carousel?.page === box?.carousel?.slides?.length - 1) {
                  box?.carousel?.slideNext();
                }
              }
            };

            document.addEventListener("keydown", onLightboxKeydown);
            document.addEventListener("wheel", onLightboxWheel);
            box.on("destroy", () => {
              document.removeEventListener("keydown", onLightboxKeydown);
              document.removeEventListener("wheel", onLightboxWheel);
            });

            document.querySelectorAll(".product__media-toggle").forEach((item) => {
              if (item.__hbMediaToggleClickBound) return;
              item.__hbMediaToggleClickBound = true;
              item.addEventListener(
                "click",
                function (event) {
                  event.stopPropagation();
                  event.preventDefault();
                },
                true,
              );
            });
          })
          .catch((error) => console.error(error))
          .finally(() => {
            productMediaList.__hbLightboxOpening = false;
          });
      }
    };

    productMediaList.addEventListener("mouseup", openLightbox);
    productMediaList.addEventListener(
      "click",
      (event) => {
        const triggerEl = event.target.closest?.(".product__media-toggle");
        if (event.detail !== 0 || !triggerEl) return;
        event.preventDefault();
        event.stopPropagation();
        openLightbox({ button: 0 }, triggerEl);
      },
      true,
    );
  });
}

document.addEventListener("DOMContentLoaded", function () {
  applyThumbnailSliderEnhancements();
  applyProductMediaEnhancements();
});
